<template>
  <div class="container">
    <main class="main-layout">
      <section class="card upload-section left-card">
        <div class="card-header" style="display: flex; justify-content: center; align-items: center; height: 80px;">
          <h2>Video Upload</h2>
        </div>
        <div class="card-body">
          <form @submit.prevent="handleSubmit" class="upload-form">
            <label for="videoInput" class="file-label">
              <span class="file-icon">
                <i class="fas fa-upload"></i>
              </span>
              <span class="file-text" v-if="!videoFile">Click or drag video here to upload</span>
              <span class="file-text" v-if="videoFile">{{ videoFile.name }}</span>
              <input 
                type="file" 
                id="videoInput" 
                name="video" 
                accept="video/*" 
                required
                @change="handleVideoChange"
                class="file-input"
              >
            </label>
            <button type="submit" class="btn btn-primary upload-btn" :disabled="!videoFile || isLoading">
              <span v-if="!isLoading">Upload and Segment</span>
              <span v-if="isLoading">
                <i class="fas fa-spinner fa-spin"></i> Processing...
              </span>
            </button>
          </form>
        </div>
      </section>

      <section class="card image-compare-section right-card">
        <div class="card-header" style="display: flex; justify-content: center; align-items: center; height: 80px;">
          <h2>Original & Segmentation</h2>
        </div>
        <div class="card-body">
          <div class="image-compare-container">
            <div class="image-item">
              <h3 class="image-title">Original</h3>
              <div class="image-box">
                <video 
                  v-if="videoPreviewUrl" 
                  :src="videoPreviewUrl" 
                  class="display-image" 
                  controls
                  ref="originalVideo"
                ></video>
                <div v-else class="image-placeholder">No video uploaded</div>
              </div>
            </div>

            <div class="image-item">
              <h3 class="image-title">Segmentation</h3>
              <div class="image-box">
                <canvas class="display-image" ref="segCanvas"></canvas>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    
    <footer class="page-footer">
      <p> Pig Segmentation System &copy; 2025</p>
    </footer>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const videoFile = ref(null);
const videoPreviewUrl = ref('');
const isLoading = ref(false);

const originalVideo = ref(null);
const segCanvas = ref(null);

const handleVideoChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    videoFile.value = file;
    videoPreviewUrl.value = URL.createObjectURL(file);
  }
};

const handleSubmit = async () => {
  if (!videoFile.value) return;
  isLoading.value = true;

  const video = originalVideo.value;
  const canvas = segCanvas.value;
  const ctx = canvas.getContext('2d');

  await new Promise(resolve => {
    video.onloadeddata = resolve;
    video.load();
  });

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const totalSeconds = Math.floor(video.duration);

  for (let second = 0; second < totalSeconds; second++) {
    video.currentTime = second;
    await new Promise(resolve => { video.onseeked = resolve; });

    // --------------------------
    // 👇 关键修改：不先画原图，避免闪烁
    // --------------------------

    // 直接把当前帧画到一个离屏画布（不显示在页面）
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    tempCtx.drawImage(video, 0, 0);

    // 发送请求
    const blob = await new Promise(resolve => tempCanvas.toBlob(resolve, 'image/jpeg'));
    const fd = new FormData();
    fd.append('image', blob);

    try {
      const res = await fetch('https://fengguoheng.online/segment', {
        method: 'POST',
        body: fd
      });
      const data = await res.json();

      if (data.overlay_image) {
        const img = new Image();
        await new Promise(resolve => {
          img.onload = resolve;
          img.src = 'data:image/jpeg;base64,' + data.overlay_image;
        });
        
        // 👇 只在这里更新画面 → 结果一直保持显示
        ctx.clearRect(0,0,canvas.width,canvas.height);
        ctx.drawImage(img, 0,0);
      }
    } catch (e) {}
  }

  isLoading.value = false;
};
</script>

<style scoped>
.container {
  font-family: 'Segoe UI', Roboto, sans-serif;
  max-width: 1600px;
  margin: 0 auto;
  padding: 20px;
  background: #f8fafc;
  min-height: 100vh;
}

.main-layout {
  display: flex;
  gap: 25px;
  align-items: stretch;
}

.left-card {
  width: 380px;
  flex-shrink: 0;
}

.right-card {
  flex: 1;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
  overflow: hidden;
}

.card-header {
  background: #f1f5f9;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  text-align: center;
}

.card-header h2 {
  margin: 0;
  font-size: 1.3rem;
  color: #1e293b;
}

.card-body {
  padding: 24px;
}

.upload-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.file-label {
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 40px 15px;
  text-align: center;
  cursor: pointer;
  background: #f8fafc;
  color: #64748b;
}

.file-label:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.file-icon {
  font-size: 2.2rem;
  margin-bottom: 10px;
}

.file-input {
  display: none;
}

.upload-btn {
  padding: 12px;
  width: 100%;
  font-size: 1rem;
}

.btn {
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.image-compare-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 25px;
  height: 100%;
}

.image-item {
  flex: 1;
  text-align: center;
}

.image-title {
  font-size: 1.2rem;
  margin-bottom: 12px;
  color: #334155;
  font-weight: 600;
}

.image-box {
  width: 100%;
  height: 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
}

.display-image {
  max-width: 100%;
  max-height: 450px;
  object-fit: contain;
  border-radius: 6px;
}

.page-footer {
  margin-top: 30px;
  text-align: center;
  color: #94a3b8;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 1024px) {
  .main-layout {
    flex-direction: column;
  }
  .left-card {
    width: 100%;
  }
}
</style>